import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../../../../auth/AuthContext";
import { db } from "../../../../configs/firebase";
import {
  USER_ROLES,
  normalizeUserRole,
} from "../../../Profile/constants/userRoles";

export type AdminAccessScope = {
  userId: string;
  role: string;
  isSuperAdmin: boolean;
  hasAdminAccess: boolean;
  loading: boolean;
  ownsRecord: (record: unknown) => boolean;
  scopeParams: <T extends Record<string, unknown>>(
    params?: T,
  ) => T & {
    accountUserId?: string;
    ownerUserId?: string;
  };
};

const EMPTY_SCOPE: AdminAccessScope = {
  userId: "",
  role: USER_ROLES.GUEST,
  isSuperAdmin: false,
  hasAdminAccess: false,
  loading: true,
  ownsRecord: () => false,
  scopeParams: (params: any = {}) => params,
};

let runtimeScope = {
  userId: "",
  role: USER_ROLES.GUEST as string,
  isSuperAdmin: false,
};

const clean = (value: unknown) => String(value || "").trim();

const normalizeRole = (value: any) =>
  clean(normalizeUserRole(value)).toLowerCase();

const collectRecordOwnerIds = (record: any): Set<string> => {
  const ids = new Set<string>();

  const add = (value: unknown) => {
    const id = clean(value);
    if (id) ids.add(id);
  };

  if (!record || typeof record !== "object") return ids;

  add(record.ownerUserId);
  add(record.sellerUserId);
  add(record.accountUserId);
  add(record.businessUserId);
  add(record.createdByUserId);
  add(record.createdBy);
  add(record.userId);
  add(record.driverOwnerUserId);
  add(record.warehouseOwnerUserId);

  add(record.assignment?.ownerUserId);
  add(record.warehouse?.ownerUserId);
  add(record.meta?.ownerUserId);
  add(record.metadata?.ownerUserId);

  if (Array.isArray(record.items)) {
    record.items.forEach((item: any) => {
      add(item?.ownerUserId);
      add(item?.sellerUserId);
      add(item?.ownerId);
      add(item?.userId);
    });
  }

  return ids;
};

export const getRuntimeAdminAccessScope = () => ({ ...runtimeScope });

export const mergeRuntimeScopeParams = <
  T extends Record<string, unknown> = Record<string, unknown>,
>(
  params?: T,
): T & { accountUserId?: string; ownerUserId?: string } => {
  const merged = { ...(params || ({} as T)) } as T & {
    accountUserId?: string;
    ownerUserId?: string;
  };

  if (!runtimeScope.isSuperAdmin && runtimeScope.userId) {
    merged.accountUserId = runtimeScope.userId;
    merged.ownerUserId = runtimeScope.userId;
  }

  return merged;
};

export const mergeRuntimeScopeBody = <
  T extends Record<string, unknown> = Record<string, unknown>,
>(
  body?: T,
): T & { accountUserId?: string; ownerUserId?: string } => {
  const merged = { ...(body || ({} as T)) } as T & {
    accountUserId?: string;
    ownerUserId?: string;
  };

  if (!runtimeScope.isSuperAdmin && runtimeScope.userId) {
    merged.accountUserId = runtimeScope.userId;
    merged.ownerUserId = runtimeScope.userId;
  }

  return merged;
};

export const runtimeScopeOwnsRecord = (record: unknown) => {
  if (runtimeScope.isSuperAdmin) return true;
  if (!runtimeScope.userId) return false;

  return collectRecordOwnerIds(record).has(runtimeScope.userId);
};

export const filterRowsForRuntimeScope = <T,>(rows: T[]): T[] => {
  if (runtimeScope.isSuperAdmin) return rows;
  return rows.filter(runtimeScopeOwnsRecord);
};

const AdminAccessContext = createContext<AdminAccessScope>(EMPTY_SCOPE);

export const AdminAccessProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const [role, setRole] = useState<string>(USER_ROLES.GUEST);
  const [loading, setLoading] = useState(true);

  const userId = clean(user?.uid);

  useEffect(() => {
    let active = true;

    const loadRole = async () => {
      if (!userId) {
        if (active) {
          setRole(USER_ROLES.GUEST);
          setLoading(false);
        }
        return;
      }

      setLoading(true);

      try {
        const snapshot = await getDoc(doc(db, "users", userId));
        const firestoreRole = snapshot.exists()
          ? snapshot.data()?.role
          : undefined;

        const resolvedRole = normalizeUserRole(
          firestoreRole ||
            (user as any)?.role ||
            (user as any)?.userRole ||
            (user as any)?.claims?.role ||
            USER_ROLES.GUEST,
        );

        if (active) setRole(resolvedRole);
      } catch (error) {
        console.error("Failed to resolve admin access role:", error);
        if (active) setRole(USER_ROLES.GUEST);
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadRole();

    return () => {
      active = false;
    };
  }, [user, userId]);

  const normalizedRole = normalizeRole(role);
  const isSuperAdmin = normalizedRole === normalizeRole(USER_ROLES.SUPER_ADMIN);
  const hasAdminAccess =
    isSuperAdmin || normalizedRole === normalizeRole(USER_ROLES.ADMIN);

  // Keep the module-level scope in sync during render so RTK Query children
  // cannot issue their first request before the provider effect runs.
  runtimeScope = {
    userId,
    role: normalizedRole,
    isSuperAdmin,
  };

  const ownsRecord = useCallback(
    (record: unknown) => {
      if (isSuperAdmin) return true;
      if (!userId) return false;
      return collectRecordOwnerIds(record).has(userId);
    },
    [isSuperAdmin, userId],
  );

  const scopeParams = useCallback(
    <T extends Record<string, unknown>>(params?: T) => {
      const merged = { ...(params || ({} as T)) } as T & {
        accountUserId?: string;
        ownerUserId?: string;
      };

      if (!isSuperAdmin && userId) {
        merged.accountUserId = userId;
        merged.ownerUserId = userId;
      }

      return merged;
    },
    [isSuperAdmin, userId],
  );

  const value = useMemo<AdminAccessScope>(
    () => ({
      userId,
      role: normalizedRole,
      isSuperAdmin,
      hasAdminAccess,
      loading,
      ownsRecord,
      scopeParams,
    }),
    [
      hasAdminAccess,
      isSuperAdmin,
      loading,
      normalizedRole,
      ownsRecord,
      scopeParams,
      userId,
    ],
  );

  return (
    <AdminAccessContext.Provider value={value}>
      {children}
    </AdminAccessContext.Provider>
  );
};

export const useAdminAccess = () => useContext(AdminAccessContext);
